<template>
  <b-container class="square-terminal-device-list">
    <loading-spinner v-if="loading">
      <div slot="title">
        Please wait loading Square terminal devices
      </div>
    </loading-spinner>

    <b-row v-if="noDevices && !loading">
      <b-col>
        <div class="no-devices">
          <div class="icon-container">
            <font-awesome-icon
              icon="hard-drive"
              size="3x"
              class="icon"
              fixed-width
            />
          </div>
          <div class="message">
            There are no terminal devices configured. Please add one.
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="!loading">
      <b-col>
        <div class="device-list-wrapper">
          <div
            v-for="(device, idx) in devices"
            :key="idx"
            class="device"
          >
            <square-terminal-device-details
              :device="device"
              :remove="true"
              @removed="deviceRemoved"
              @error="deviceRemoveError"
            />
          </div>
        </div>
        <b-row v-if="message">
          <b-col>
            <div
              class="message"
              :class="message.type"
              @click="dismissMessages"
            >
              <div class="inner">
                <font-awesome-icon
                  v-if="!errorMessage"
                  icon="check-circle"
                  size="2x"
                  class="icon"
                  fixed-width
                  color="green"
                />
                <font-awesome-icon
                  v-if="errorMessage"
                  icon="triangle-exclamation"
                  size="2x"
                  class="icon"
                  fixed-width
                  color="red"
                />
                {{ message.text }}
              </div>
            </div>
          </b-col>
        </b-row>
        <div
          v-if="!showAddDeviceForm"
          class="add-terminal-device"
        >
          <b-button
            v-b-modal.add-device-modal
            variant="primary"
            @click="addTerminalDevice"
          >
            Add terminal device
          </b-button>
        </div>

        <div
          v-if="showAddDeviceForm"
          id="add-device-modal"
        >
          <SquareTerminalAddDevice
            :allow-simulated-reader="allowSimulatedReader"
            @success="newDeviceAdded"
            @error="newDeviceError"
            @cancel="showAddDeviceForm = false"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import SquareTerminalDeviceDetails from './SquareTerminalDeviceDetails.vue'
import SquareTerminalAddDevice from './SquareTerminalAddDevice.vue'

export default {
  name: 'square-terminal-device-list',
  components: {
    SquareTerminalDeviceDetails,
    SquareTerminalAddDevice
  },
  props: {
    allowSimulatedReader: {
      type: Boolean,
      required: false,
      defaultValue: false
    }
  },
  data: function() {
    return {
      loading: false,
      showAddDeviceForm: false,
      message: null
    }
  },
  computed: {
    noDevices: function() {
      return this.devices.length === 0
    },
    devices: function() {
      return this.$store.state.square.terminalDevices
    },
    errorMessage: function() {
      return (this.message && this.message.type === 'error')
    }
  },
  watch: {
    loading: function(newVal) {
      this.$emit('loading', newVal)
    }
  },
  mounted: function() {
    this.loading = true
    return Promise.all([
      this.$store.dispatch('square/loadDevicesList', { force: true }),
      this.$store.dispatch('square/loadLocations', { force: true })
    ])
      .finally(_ => {
        this.loading = false
      })
  },
  methods: {
    displayMessage: function(message, type, timeout) {
      this.message = {
        type: (type === undefined) ? 'success' : 'error',
        text: message
      }
      setTimeout(() => {
        this.message = null
      }, timeout === undefined ? 15000 : timeout)
    },
    dismissMessages: function(type) {
      this.message = null
    },
    newDeviceAdded: function(device) {
      this.showAddDeviceForm = false
      this.displayMessage(`New device ${device.label} was successfully added.`)
    },
    newDeviceError: function({ device, error }) {
      this.showAddDeviceForm = false
      this.displayMessage(`Error adding new location ${device.label} -- ${error.message}`, 'error')
    },
    deviceRemoved: function(device) {
      this.displayMessage(`Device ${device.id} - ${device.label} was removed successfully.`)
    },
    deviceRemoveError: function({ device, error }) {
      this.displayMessage(
        `Device  ${device.id} - ${device.label} could not be removed.<br>Reason: ${error.message}`,
        'error'
      )
    },
    addTerminalDevice: function() {
      this.showAddDeviceForm = true
    }
  }
}
</script>

<style lang="scss" scoped>
  .message {
    padding: 1.2rem 0.8rem;
    &.error {
      .inner {
        background-color: lightpink;
        color: darkred;
      }
    }

    &.success {
      .inner {
        background-color: lightgreen;
        color: darkgreen;
      }
    }

    .inner {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 1.2rem 0.5rem;
      div {
        margin-left: 2.2rem;
      }
    }
  }

  .square-terminal-device-list {
    .no-devices {
      display: flex;
      flex-direction: row;
      justify-content: center;
      padding: 2.2rem;
      align-items: center;
    }

    .device-list-wrapper {
      padding: 0;
      list-style: none;
      display: grid;
      grid-auto-columns: 1fr;
      grid-auto-rows: 1fr;
      grid-template-columns: repeat(2, 1fr);
      gap: .5rem;
    }

    .add-terminal-device {
      margin-top: 2.2rem;
    }
  }
</style>
